
import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  NgZone,
} from '@angular/core';

import {
  ActivatedRoute,
  NavigationEnd,
  Router,
} from '@angular/router';

import { ChangePasswordDialogComponent } from '@components/change-password-dialog/change-password-dialog.component';
import { RegulationDialogComponent } from '@components/regulation-dialog/regulation-dialog.component';

import { ContactDto } from '@models/users/contact.dto';

import { AccountService } from '@services/account.service';
import { ContactService } from '@services/contact.service';
import { RegulationService } from '@services/regulation.service';

import { DeviceDetectorService } from 'ngx-device-detector';

import {
  DialogService,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';

import { finalize, Subscription } from 'rxjs';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
})
export class AppComponent
  implements OnInit, AfterViewInit, OnDestroy {

  title = 'ebs-trade-union-app';

  public get isLogged(): boolean {
    return this.accountService.isLoggedIn;
  }

  public get isSmartphone(): boolean {
    return this.deviceService.isMobile();
  }

  public get isInfoPage(): boolean {
    return (
      this.router.url.split('?')[0].split('#')[0] ===
      '/infoPage'
    );
  }

  public isVisible: boolean = false;
  public isShown: boolean = true;

  contacts: ContactDto[] = [];

  private routerSubscription?: Subscription;
  private scrollObserver?: MutationObserver;

  private scrollListeners = new Map<
    HTMLElement,
    EventListener
  >();

  constructor(
    public router: Router,
    private activatedRoute: ActivatedRoute,
    private deviceService: DeviceDetectorService,
    private accountService: AccountService,
    private regulationService: RegulationService,
    public dialogService: DialogService,
    private contactService: ContactService,
    private ngZone: NgZone,
  ) {}

  ngOnInit(): void {

    /*
     * Gestione della navigazione.
     *
     * Quando si cambia pagina:
     * - viene ripristinata la visibilità della toolbar;
     * - viene riportato lo scroll all'inizio;
     * - vengono registrati gli eventuali nuovi
     *   contenitori scrollabili.
     */
    this.routerSubscription = this.router.events.subscribe(
      (event) => {
        if (event instanceof NavigationEnd) {
          this.isShown = true;

          this.resetPageScroll();

          this.ngZone.runOutsideAngular(() => {
            requestAnimationFrame(() => {
              this.resetPageScroll();
              this.registerScrollListeners();
            });
          });
        }
      },
    );

    if (this.accountService.isLoggedIn) {
      this.accountService.getUserInfo().subscribe((result) => {

        if (result.mustChangePassword) {
          let dialogRef: DynamicDialogRef = this.dialogService.open(
            ChangePasswordDialogComponent,
            {
              maximizable: false,
              draggable: false,
              closable: false,
              closeOnEscape: false,
              header: 'Password scaduta',
            },
          );

          dialogRef.onClose.subscribe((result) => {
            if (!result) {
              return;
            }

            this.accountService.changePassword(result).subscribe();
          });
        }

        if (result.emptyRegulations.length > 0) {
          let dialogRef: DynamicDialogRef = this.dialogService.open(
            RegulationDialogComponent,
            {
              maximizable: false,
              draggable: false,
              closable: false,
              closeOnEscape: false,
              header: 'Consensi',
              data: {
                regulations: result.emptyRegulations,
              },
            },
          );

          dialogRef.onClose.subscribe((result) => {
            if (!result) {
              return;
            }

            this.regulationService.acceptRegulation(result).subscribe();
          });
        }
      });
    } else {
      //this.router.navigateByUrl('/login');
      //Tolto reindirizzamento automatico a login per permettere l'accesso alla pagina infoPage anche senza login
    }
  }

  ngAfterViewInit(): void {
    this.registerScrollListeners();

    /*
     * Osserva i cambiamenti del DOM per individuare
     * eventuali contenitori scrollabili caricati
     * dopo la navigazione.
     *
     * I listener vengono aggiunti soltanto agli
     * elementi che non ne possiedono già uno.
     */
    this.ngZone.runOutsideAngular(() => {
      this.scrollObserver = new MutationObserver(() => {
        this.registerScrollListeners();
      });

      this.scrollObserver.observe(
        document.body,
        {
          childList: true,
          subtree: true,
        },
      );
    });
  }

  /**
   * Riporta la pagina in alto.
   *
   * Gestisce sia lo scroll della finestra
   * sia gli eventuali contenitori scrollabili
   * utilizzati dall'applicazione.
   */
  private resetPageScroll(): void {
    window.scrollTo(0, 0);

    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    const containers = document.querySelectorAll<HTMLElement>(
      '.main-content-desktop, ' +
      '.main-content-mobile, ' +
      '.public-page-container, ' +
      '.p-scrollpanel-content, ' +
      '.p-datatable-wrapper, ' +
      '.p-scroller-inline',
    );

    containers.forEach((container) => {
      container.scrollTop = 0;
    });
  }

  /**
   * Gestione dello scroll su smartphone.
   *
   * Comportamento originale mantenuto:
   * - scorrendo verso il basso viene nascosta
   *   la toolbar;
   * - scorrendo verso l'alto viene mostrata;
   * - tornando all'inizio viene mostrata.
   *
   * Rispetto alla versione precedente,
   * evita di registrare più listener
   * sullo stesso elemento.
   */
  private registerScrollListeners(): void {
    const selectors = [
      '.p-scrollpanel-content',
      '.p-scroller-inline',
      '.p-datatable-wrapper',
    ].join(', ');

    const elements =
      document.querySelectorAll<HTMLElement>(selectors);

    elements.forEach((element) => {
      if (this.scrollListeners.has(element)) {
        return;
      }

      let lastScrollTop = element.scrollTop;

      const listener: EventListener = () => {
        if (!this.isSmartphone) {
          return;
        }

        const currentScrollTop = element.scrollTop;

        const difference =
          currentScrollTop - lastScrollTop;

        if (currentScrollTop <= 0) {
          this.updateToolbarVisibility(true);
          lastScrollTop = currentScrollTop;
          return;
        }

        if (difference > 40) {
          this.updateToolbarVisibility(false);
          lastScrollTop = currentScrollTop;
        } else if (difference < -40) {
          this.updateToolbarVisibility(true);
          lastScrollTop = currentScrollTop;
        }
      };

      element.addEventListener(
        'scroll',
        listener,
        { passive: true },
      );

      this.scrollListeners.set(element, listener);
    });

    // Rimuove i listener degli elementi
    // che non sono più presenti nel DOM.
    this.scrollListeners.forEach((listener, element) => {
      if (!element.isConnected) {
        element.removeEventListener('scroll', listener);
        this.scrollListeners.delete(element);
      }
    });
  }

  /**
   * Aggiorna la visibilità della toolbar
   * all'interno del contesto Angular.
   */
  private updateToolbarVisibility(
    visible: boolean,
  ): void {
    if (this.isShown === visible) {
      return;
    }

    this.ngZone.run(() => {
      this.isShown = visible;
    });
  }

  public logout() {
    this.accountService
      .logout()
      .pipe(
        finalize(() => {
          //this.router.navigateByUrl('/login');
          this.router.navigateByUrl('/infoPage');
        }),
      )
      .subscribe();
  }

  public openOrgChartView() {
    /* let dialogRef: DynamicDialogRef = this.dialogService.open(OrganizationChartViewComponent, {
      maximizable: false,
      draggable: false,
      height: '100%',
      width: '100%',
      closable: true,
      closeOnEscape: false
    }); */

    this.contactService.getContactView().subscribe((result) => {
      //this.contacts = result;
      this.contacts = [...result].sort((a, b) => a.position - b.position); //ordinamento
      this.isVisible = true;
    });
  }

  /**
   * Pulizia delle risorse alla distruzione
   * del componente.
   *
   * Evita listener duplicati e possibili
   * perdite di memoria.
   */
  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();

    this.scrollObserver?.disconnect();

    this.scrollListeners.forEach(
      (listener, element) => {
        element.removeEventListener(
          'scroll',
          listener,
        );
      },
    );

    this.scrollListeners.clear();
  }
}
